import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchIcon from "@mui/icons-material/Search";
import SidePanel from "layouts/SidePanel";
import rfqService from "services/rfqService";
import { useSnackbar } from "notistack";

const FEATURE_NAME = "MATERIAL_MASTER_LIST";
const PAGE_SIZE = 100;

const emptyForm = {
  code: "",
  name: "",
  unit: "",
  materialType: "",
  materialSubType: "",
  category: "",
  brand: "",
  specification: "",
};

const hasFeature = (features = []) =>
  features.some((feature) => feature?.featureName === FEATURE_NAME && feature?.hasFeature === true);

const getUserPayload = (user = {}) => ({
  userId: user?.userId,
  name: user?.name || user?.userProfile?.name,
});

const getCreatedByName = (material = {}) =>
  material?.createdBy?.name || material?.createdBy?.userId || "-";

const getUpdatedByName = (material = {}) =>
  material?.updatedBy?.name || material?.updatedBy?.userId || "-";

const getEntitySlug = (name = "entity") =>
  encodeURIComponent(
    String(name || "entity")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "entity"
 );

const sortMaterialsByName = (list = []) =>
  [...list].sort((first, second) => String(first?.name || "").localeCompare(String(second?.name || "")));

const MaterialMasterPage = () => {
  const user = useSelector((state) => state.auth?.user);
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams();
  const entityId = user?.userEntity?.entityId;
  const canAccess = hasFeature(user?.userEntity?.features || []);
  const entityName = user?.userEntity?.entityName || user?.userEntity?.name || entityId || "Entity";
  const materialMasterPath = entityId ? "/material-master/" + getEntitySlug(entityName) + "/" + entityId : "/material-master";
  const isSharedEntityMismatch = Boolean(routeParams?.entityId && entityId && routeParams.entityId !== entityId);
  const hasMaterialMasterAccess = canAccess && !isSharedEntityMismatch;
  const { enqueueSnackbar } = useSnackbar();

  const [materials, setMaterials] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formValues, setFormValues] = useState(emptyForm);

  const totalPages = Math.max(Math.ceil(totalCount / PAGE_SIZE), 1);
  const sortedMaterials = useMemo(() => sortMaterialsByName(materials), [materials]);

  const loadMaterials = async ({ nextPage = page, nextSearch = search } = {}) => {
    if (!entityId || !hasMaterialMasterAccess) return;

    setLoading(true);
    try {
      const response = await rfqService.getMaterialMaster({
        entityId,
        page: nextPage,
        limit: PAGE_SIZE,
        search: nextSearch,
      });
      const payload = response?.payload || response;
      setMaterials(payload?.materials || []);
      setTotalCount(payload?.totalCount || 0);
    } catch (error) {
      enqueueSnackbar("Could not fetch material master list.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

 useEffect(() => {
 if (!entityId || isSharedEntityMismatch) return;

    if (location.pathname === "/material-master" || (routeParams?.entityId && location.pathname !== materialMasterPath)) {
      navigate(materialMasterPath, { replace: true });
 }
 }, [entityId, isSharedEntityMismatch, location.pathname, materialMasterPath, navigate, routeParams?.entityId]);

 useEffect(() => {
 loadMaterials({ nextPage: page });
 }, [entityId, hasMaterialMasterAccess, page]);

  const openCreateForm = () => {
    setEditingMaterial(null);
    setFormValues(emptyForm);
    setFormOpen(true);
  };

  const openEditForm = (material) => {
    setEditingMaterial(material);
    setFormValues({
      code: material?.code || "",
      name: material?.name || "",
      unit: material?.unit || "",
      materialType: material?.materialType || "",
      materialSubType: material?.materialSubType || "",
      category: material?.category || "",
      brand: material?.brand || "",
      specification: material?.specification || "",
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingMaterial(null);
    setFormValues(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formValues.name.trim()) {
      enqueueSnackbar("Material name is required.", { variant: "warning" });
      return;
    }

    const userPayload = getUserPayload(user);
    const payload = {
      ...formValues,
      entityId,
      createdBy: userPayload,
      updatedBy: userPayload,
    };

    try {
      if (editingMaterial?._id) {
        await rfqService.updateMaterialMaster({ id: editingMaterial._id, ...payload });
        enqueueSnackbar("Material updated successfully.", { variant: "success" });
      } else {
        await rfqService.createMaterialMaster(payload);
        enqueueSnackbar("Material added successfully.", { variant: "success" });
      }
      closeForm();
      loadMaterials({ nextPage: page });
    } catch (error) {
      enqueueSnackbar("Could not save material.", { variant: "error" });
    }
  };

  const handleDelete = async (material) => {
    if (!material?._id) return;
    if (!window.confirm(`Delete ${material.name}?`)) return;

    try {
      await rfqService.deleteMaterialMaster({
        id: material._id,
        entityId,
        updatedBy: getUserPayload(user),
      });
      enqueueSnackbar("Material deleted successfully.", { variant: "success" });
      loadMaterials({ nextPage: page });
    } catch (error) {
      enqueueSnackbar("Could not delete material.", { variant: "error" });
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    loadMaterials({ nextPage: 1, nextSearch: search });
  };

  const pageSubtitle = useMemo(() => {
    if (!totalCount) return "No active materials available.";
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, totalCount);
    return `Showing ${from}-${to} of ${totalCount} active materials`;
  }, [page, totalCount]);

  const formFields = [
    ["code", "Code", "Material code"],
    ["name", "Material name *", "Material name"],
    ["unit", "Unit", "UOM"],
    ["materialType", "Material Type", "Material type"],
    ["materialSubType", "Material SubType", "Material subtype"],
    ["category", "Category", "Optional category"],
    ["brand", "Brand", "Optional brand"],
  ];

  return (
    <SidePanel>
      <div className="min-h-screen bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF]">
        <div className="overflow-hidden rounded-[28px] border border-orange-100 bg-white/95 shadow-[0_18px_45px_rgba(31,30,41,0.08)] backdrop-blur">
          <div className="relative overflow-hidden border-b px-7 py-6">
            <div className="absolute right-0 top-0 h-36 w-36 rounded-bl-full bg-orange-100/80" />
            <div className="absolute right-24 top-8 h-16 w-16 rounded-full bg-blue-100/70" />
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1F1E29] text-white shadow-lg shadow-orange-100">
                  <Inventory2OutlinedIcon fontSize="medium" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Entity master for - {entityName}</p>
                  <h1 className="mt-1 text-2xl font-bold text-[#1F1E29]">Material Master List</h1>
                </div>
              </div>
              {hasMaterialMasterAccess && (
                <button
                  type="button"
                  onClick={openCreateForm}
                  className="inline-flex items-center gap-2 rounded-full bg-[#1F1E29] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-gray-200 transition hover:-translate-y-0.5 hover:bg-black"
                >
                  <AddIcon fontSize="small" />
                  Add material
                </button>
              )}
            </div>
          </div>

          {!hasMaterialMasterAccess ? (
            <div className="px-6 py-16 text-center text-gray-500">
              Material master access is not enabled for this entity, or this shared link belongs to another entity.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 px-7 py-5">
                <div>
                  <p className="text-sm font-semibold text-[#1F1E29]">{pageSubtitle}</p>
                  <p className="mt-1 text-xs text-gray-500">Sorted alphabetically by material name. Showing {PAGE_SIZE} rows per page.</p>
                </div>
                <form onSubmit={handleSearch} className="flex min-w-[360px] max-w-2xl flex-1 justify-end gap-2">
                  <div className="relative w-full max-w-xl">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fontSize="small" />
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search material, code, type, brand..."
                      className="h-11 w-full rounded-full border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm shadow-inner focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-100"
                    />
                  </div>
                  <button type="submit" className="rounded-full border border-orange-300 px-5 text-sm font-semibold text-orange-700 transition hover:bg-orange-50">
                    Search
                  </button>
                </form>
              </div>

              <div className="px-7 pb-7">
                <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1400px] text-sm">
                      <thead className="bg-[#FFF8F2] text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
                        <tr>
                          <th className="px-4 py-4">Code</th>
                          <th className="px-4 py-4">Material Type</th>
                          <th className="px-4 py-4">Material SubType</th>
                          <th className="px-4 py-4">Material Name</th>
                          <th className="px-4 py-4">Unit</th>
                          <th className="px-4 py-4">Category</th>
                          <th className="px-4 py-4">Brand</th>
                          <th className="px-4 py-4">Specification</th>
                          <th className="whitespace-nowrap px-4 py-4">Created By</th>
                          <th className="whitespace-nowrap px-4 py-4">Updated By</th>
                          <th className="px-4 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {loading ? (
                          <tr><td colSpan="11" className="px-4 py-12 text-center text-gray-500">Loading material master...</td></tr>
                        ) : sortedMaterials.length ? sortedMaterials.map((material) => (
                          <tr key={material._id} className="transition hover:bg-orange-50/50">
                            <td className="px-4 py-4 font-medium text-gray-600">{material.code || "-"}</td>
                            <td className="min-w-[220px] px-4 py-4 align-middle">
                              <span className="inline-flex max-w-[260px] whitespace-normal break-words rounded-xl bg-blue-50 px-3 py-1.5 text-xs font-semibold leading-5 text-blue-700">{material.materialType || "-"}</span>
                            </td>
                            <td className="px-4 py-4">{material.materialSubType || "-"}</td>
                            <td className="px-4 py-4">
                              <div className="font-semibold text-[#1F1E29]">{material.name}</div>
                              {material.externalId && <div className="text-xs text-gray-400">ID: {material.externalId}</div>}
                            </td>
                            <td className="px-4 py-4">{material.unit || "-"}</td>
                            <td className="px-4 py-4">{material.category || "-"}</td>
                            <td className="px-4 py-4">{material.brand || "-"}</td>
                            <td className="max-w-[280px] truncate px-4 py-4 text-gray-600" title={material.specification || ""}>{material.specification || "-"}</td>
                            <td className="px-4 py-4 text-gray-600">{getCreatedByName(material)}</td>
                            <td className="px-4 py-4 text-gray-600">{getUpdatedByName(material)}</td>
                            <td className="px-4 py-4 text-right">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => openEditForm(material)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-100 bg-blue-50 text-blue-700 transition hover:border-blue-200 hover:bg-blue-100"
                                  title="Edit material"
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(material)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-600 transition hover:border-red-200 hover:bg-red-100"
                                  title="Delete material"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="11" className="px-4 py-12 text-center text-gray-500">No materials found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t bg-[#FFFBF7] px-7 py-4">
                <button type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(value - 1, 1))} className="rounded-full border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Previous</button>
                <span className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm">Page {page} of {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-full border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Next</button>
              </div>
            </>
          )}
        </div>

        {formOpen && (
          <div className="fixed inset-0 z-[1200] flex justify-end bg-black/30 backdrop-blur-[2px]">
            <form onSubmit={handleSubmit} className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl">
              <div className="border-b bg-gradient-to-r from-[#FFF8F2] to-white px-7 py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[#1F1E29]">{editingMaterial ? "Edit material" : "Add material"}</h2>
                    <p className="mt-1 text-sm text-gray-500">This item will be available in RFQ material selection.</p>
                  </div>
                  <button type="button" onClick={closeForm} className="rounded-full px-3 py-1 text-gray-500 hover:bg-gray-100">x</button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 px-7 py-6 md:grid-cols-2">
                {formFields.map(([key, label, placeholder]) => (
                  <label key={key} className="text-sm font-semibold text-gray-700">
                    {label}
                    <input
                      value={formValues[key]}
                      placeholder={placeholder}
                      onChange={(event) => setFormValues((previous) => ({ ...previous, [key]: event.target.value }))}
                      className="mt-1 h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-normal focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-100"
                    />
                  </label>
                ))}
                <label className="text-sm font-semibold text-gray-700 md:col-span-2">
                  Specification
                  <textarea
                    value={formValues.specification}
                    placeholder="Material specification"
                    onChange={(event) => setFormValues((previous) => ({ ...previous, specification: event.target.value }))}
                    rows="4"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-normal focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
              </div>
              <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white px-7 py-4">
                <button type="button" onClick={closeForm} className="rounded-full border px-5 py-2 text-sm font-semibold">Cancel</button>
                <button type="submit" className="rounded-full bg-[#1F1E29] px-5 py-2 text-sm font-semibold text-white hover:bg-black">Save material</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </SidePanel>
  );
};

export default MaterialMasterPage;
